import type { HistoryEntry } from '../state/historyStore';
import type { StyleOption } from '../../../shared/api/contracts/styleTransferSchemas';
import { compressDataUrl, compressFileToDataUrl } from '../../../shared/utils/compressImage';

const FALLBACK_DIMENSIONS = { width: 0, height: 0 } as const;
const MAX_DIMENSION = 2048;
const ORIGINAL_QUALITY = 0.86;
const RESULT_QUALITY = 0.88;

function generateHistoryId(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto.getRandomValues === 'function') {
      const buffer = new Uint8Array(16);
      crypto.getRandomValues(buffer);
      buffer[6] = (buffer[6] & 0x0f) | 0x40;
      buffer[8] = (buffer[8] & 0x3f) | 0x80;
      const hex = Array.from(buffer, (value) => value.toString(16).padStart(2, '0'));
      return [
        hex.slice(0, 4).join(''),
        hex.slice(4, 6).join(''),
        hex.slice(6, 8).join(''),
        hex.slice(8, 10).join(''),
        hex.slice(10, 16).join('')
      ].join('-');
    }
  }
  return `history-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

function isBrowserEnvironment(): boolean {
  return typeof window !== 'undefined' && typeof Image !== 'undefined';
}

async function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  if (!isBrowserEnvironment()) {
    return FALLBACK_DIMENSIONS;
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => resolve(FALLBACK_DIMENSIONS);
    img.src = dataUrl;
  });
}

function estimateDataUrlBytes(dataUrl: string): number {
  if (!dataUrl) {
    return 0;
  }
  const commaIndex = dataUrl.indexOf(',');
  const base64 = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
  const paddingMatch = base64.match(/=+$/);
  const padding = paddingMatch ? paddingMatch[0].length : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

export async function buildHistoryEntry(params: {
  file: File;
  style: StyleOption;
  tokenType: 'device' | 'user';
  resultDataUrl?: string;
  resultMimeType?: string | null;
  completedAt?: string;
}): Promise<HistoryEntry> {
  const originalCompressed = await compressFileToDataUrl(params.file, {
    maxDimension: MAX_DIMENSION,
    quality: ORIGINAL_QUALITY,
    preferredMimeTypes: ['image/webp', 'image/jpeg', params.file.type].filter(
      (value): value is string => Boolean(value && value.startsWith('image/'))
    )
  });

  const originalDimensions =
    originalCompressed.width > 0 && originalCompressed.height > 0
      ? { width: originalCompressed.width, height: originalCompressed.height }
      : await getImageDimensions(originalCompressed.dataUrl);

  const resultSource = params.resultDataUrl ?? originalCompressed.dataUrl;
  const resultCompressed = await compressDataUrl(resultSource, {
    maxDimension: MAX_DIMENSION,
    quality: RESULT_QUALITY,
    preferredMimeTypes: params.resultMimeType ? [params.resultMimeType] : undefined,
    fallbackMimeType: params.resultMimeType ?? 'image/webp'
  });

  const resultDimensions =
    resultCompressed.width > 0 && resultCompressed.height > 0
      ? { width: resultCompressed.width, height: resultCompressed.height }
      : await getImageDimensions(resultCompressed.dataUrl);

  const storageBytes =
    estimateDataUrlBytes(originalCompressed.dataUrl) +
    estimateDataUrlBytes(resultCompressed.dataUrl);

  return {
    id: generateHistoryId(),
    tokenType: params.tokenType,
    style: {
      id: params.style.id,
      name: params.style.name,
      category: params.style.category,
      previewUrl: params.style.previewUrl
    },
    original: {
      fileName: params.file.name,
      dataUrl: originalCompressed.dataUrl,
      width: originalDimensions.width,
      height: originalDimensions.height
    },
    result: {
      dataUrl: resultCompressed.dataUrl,
      width: resultDimensions.width,
      height: resultDimensions.height,
      completedAt: params.completedAt ?? new Date().toISOString(),
      originalDataUrl: resultCompressed.dataUrl,
      originalWidth: resultDimensions.width,
      originalHeight: resultDimensions.height
    },
    createdAt: new Date().toISOString(),
    storageBytes
  };
}
